import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import Well from '../common/Well'
import DismissibleAlert from '../common/DismissibleAlert'
import FileInput from '../common/FileInput'
import RetailerSearch from './form/RetailerSearch'
import { csvUpload, clearCSVUploadResult } from '../../actions/diamond'
import { API_ROOT } from '../../constants'

import styles from './CSVUploadForm.module.css'

function DiamondCSVUploadForm({
  inProgress,
  error,
  isAdminUser,
  csvUploadResult,
  csvUpload,
  clearCSVUploadResult
}) {
  const navigate = useNavigate()
  const [retailerName, setRetailerName] = useState('')
  const [retailerId, setRetailerId] = useState('')
  const [file, setFile] = useState(null)
  const [validationError, setValidationError] = useState('')

  useEffect(() => {
    clearCSVUploadResult()
  }, [])

  const handleRetailerSelect = suggestion => {
    let id = suggestion.objectID
    if (suggestion.createdDay) {
      id = `${suggestion.createdDay}/${id}`
    }
    setRetailerName(suggestion.name)
    setRetailerId(id)
    setValidationError('')
  }

  const handleFileSelect = f => {
    setFile(f)
    setValidationError('')
  }

  const handleSubmit = event => {
    event.preventDefault()
    // Tell the user which required field is missing rather than leaving the
    // button inert with no feedback.
    if (isAdminUser && !retailerId) {
      setValidationError(
        'Please select an existing retailer to attach these items to.'
      )
      return
    }
    if (!file) {
      setValidationError('Please choose a CSV file to upload.')
      return
    }
    setValidationError('')
    csvUpload({ retailerName, retailerId, file })
  }

  const submitButtonText = inProgress ? (
    <span>
      <i className="fa fa-circle-o-notch fa-spin" /> Processing...
    </span>
  ) : (
    'Submit'
  )

  if (csvUploadResult) {
    const imported = csvUploadResult.get('imported')
    const failed = csvUploadResult.get('failed')
    // Rows whose internal_id the seller already had are skipped, not duplicated.
    const skipped = csvUploadResult.get('skipped') || 0
    const rawSkippedIds = csvUploadResult.get('skippedInternalIds') || []
    const skippedIds = rawSkippedIds.toJS ? rawSkippedIds.toJS() : rawSkippedIds
    // The same file sent again within a few minutes is answered from the first upload.
    const replayed = !!csvUploadResult.get('replayed')
    const rowErrors = csvUploadResult.get('errors') || []
    const hasRowErrors = Array.isArray(rowErrors)
      ? rowErrors.length > 0
      : Object.keys(rowErrors).length > 0

    return (
      <div id="content">
        <header>
          <h2>Bulk Upload (CSV File)</h2>
        </header>
        <Well>
          <header>Upload Complete</header>
          <fieldset>
            <div className={styles.resultSummary}>
              <div className={styles.resultStat}>
                <i
                  className="fa fa-check-circle"
                  style={{ color: '#5cb85c', marginRight: 6 }}
                />
                <strong>{imported}</strong> item{imported !== 1 ? 's' : ''}{' '}
                imported successfully
              </div>
              {skipped > 0 && (
                <div className={styles.resultStat}>
                  <i
                    className="fa fa-info-circle"
                    style={{ color: '#5bc0de', marginRight: 6 }}
                  />
                  <strong>{skipped}</strong> item{skipped !== 1 ? 's' : ''}{' '}
                  skipped (internal ID already in inventory)
                  {skippedIds.length > 0 && (
                    <span>
                      : {skippedIds.slice(0, 20).join(', ')}
                      {skippedIds.length > 20 ? ', …' : ''}
                    </span>
                  )}
                </div>
              )}
              {replayed && (
                <div className={styles.resultStat}>
                  <i
                    className="fa fa-info-circle"
                    style={{ color: '#5bc0de', marginRight: 6 }}
                  />
                  This file was already uploaded a moment ago; nothing new was
                  imported.
                </div>
              )}
              {failed > 0 && (
                <div className={styles.resultStat}>
                  <i
                    className="fa fa-exclamation-circle"
                    style={{ color: '#d9534f', marginRight: 6 }}
                  />
                  <strong>{failed}</strong> item{failed !== 1 ? 's' : ''} failed
                </div>
              )}
            </div>
            {hasRowErrors && (
              <div className={styles.rowErrors}>
                <strong>Row errors:</strong>
                <ul className={styles.rowErrorList}>
                  {Array.isArray(rowErrors)
                    ? rowErrors.map((msg, i) => <li key={i}>{msg}</li>)
                    : Object.entries(rowErrors).map(([row, msgs]) => (
                        <li key={row}>
                          <strong>{row}:</strong>{' '}
                          {[].concat(msgs).join(', ')}
                        </li>
                      ))}
                </ul>
              </div>
            )}
          </fieldset>
          <footer className={styles.resultActions}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/inventory')}
            >
              Go to Inventory
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => {
                clearCSVUploadResult()
                setFile(null)
              }}
            >
              Upload Another
            </button>
          </footer>
        </Well>
      </div>
    )
  }

  return (
    <div id="content">
      <header>
        <h2>Bulk Upload (CSV File)</h2>
      </header>
      <form className={classNames('smart-form', styles.form)}>
        {isAdminUser ? (
          <Well>
            <header>Attach Items to Retailer</header>
            <fieldset>
              <RetailerSearch
                data={{ retailerName, retailerId }}
                onSelect={handleRetailerSelect}
                disabled={inProgress}
                error={error}
              />
            </fieldset>
          </Well>
        ) : null}

        <Well>
          <header>Upload CSV File</header>
          <fieldset>
            <label className={classNames('label', styles.desc)}>
              You can bulk upload diamonds with a{' '}
              <strong>properly formatted CSV file</strong> (
              <a href={`${API_ROOT}/retailer/upload_csv/template`} target="_blank">
                Sample
              </a>
              ) below. For details on proper formatting, refer to our{' '}
              <a href="/files/html/csv_upload_instructions.html" target="_blank">
                CSV Formatting Guide
              </a>
              , which follows industry-standard naming conventions.
            </label>

            <label className="label">CSV File</label>
            <FileInput
              accept=".csv,text/csv"
              placeholder="Select a CSV file"
              onSelect={handleFileSelect}
            />

            {validationError && (
              <DismissibleAlert
                type="danger"
                className={styles.errorMessage}
                resetKey={validationError}
                onDismiss={() => setValidationError('')}
              >
                {validationError}
              </DismissibleAlert>
            )}
            {error && error.message && (
              <DismissibleAlert
                type="danger"
                className={styles.errorMessage}
                resetKey={error}
              >
                {error.message}
              </DismissibleAlert>
            )}
            {error && error.errors && (
              <DismissibleAlert
                type="danger"
                className={styles.errorMessage}
                resetKey={error}
              >
                <ul className={styles.rowErrorList}>
                  {error.errors.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </DismissibleAlert>
            )}
          </fieldset>
          <footer>
            <button
              type="submit"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={inProgress}
            >
              {submitButtonText}
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => navigate(-1)}
              disabled={inProgress}
            >
              Cancel
            </button>
          </footer>
        </Well>
      </form>
    </div>
  )
}

function mapStateToProps({ diamond, login }) {
  return {
    inProgress: diamond.get('inProgress'),
    error: diamond.get('error') || {},
    csvUploadResult: diamond.get('csvUploadResult'),
    isAdminUser: !!login.get('isAdminUser')
  }
}

export default connect(mapStateToProps, { csvUpload, clearCSVUploadResult })(
  DiamondCSVUploadForm
)

